// Account classification: split Mirasee staff and known test/QA accounts from
// real customers. Used by the admin Roster tab to filter noise out of the
// default view. Reusable from server, client, and cron.
//
// Distinct from server/lib/ontraport.ts `isMiraseeInternalEmail`, which
// conflates staff + test for the narrower purpose of skipping OP tagging.
// Here we want them separable so the roster can label them differently.

export type AccountKind = 'staff' | 'test' | 'member';

const STAFF_DOMAIN_SUFFIXES = [
  '@mirasee.com',
  '@my.mirasee.com',
  '@ruzuku.com',
  '@morebetterlabs.com',
];

const STAFF_EMAILS = new Set([
  'matthew.turner@yahoo.co.uk',
  'lisajbloom@story-coach.com',
  // Mirasee team members whose primary platform email is a personal address
  'barbasselin@yahoo.ca', // Barb Asselin — community
  'bhoomi.pthk@gmail.com', // Bhoomi Pathak — design
  'jim101@gmail.com', // Jim Wright — ops / calendar invites
  'elizabeth.c.alkire@gmail.com', // Lizzie Alkire — ops; enrols on her own address to see the student view
]);

const TEST_EMAIL_PATTERNS: RegExp[] = [
  /\+test/i, // canonical test alias
  /\+local/i, // local-dev alias
  /\+bulk/i, // bulk-enrollment test aliases (e.g. +bulk1, +bulk2)
  /^abe\.crystal\+/i, // Abe's test aliases (+ addressing)
  /marijatodorovic512/i, // Marija's primary test pattern
  /mayangie512/i, // Marija's secondary test pattern
  /matt.*@turndog/i, // Matt's test domain
  /@privacytestdriver\.com$/i, // email-testing service domain
  /^deleted\+/i, // soft-deleted user artifacts
];

// Email-specific test accounts that don't fit the regex patterns above —
// Marija and friends use a number of real-looking gmail addresses for
// checkout testing. Keep this list short; broader patterns belong above.
const KNOWN_TEST_EMAILS = new Set([
  'farmhouseseasonal@gmail.com',
  'bunkerlabmarketing@gmail.com',
  'bunkerlabmarketing+pptest@gmail.com',
  'farmhouseinteriorsig@gmail.com',
  'kitchenlittlehelpers@gmail.com',
  'marija.todorovic@mayple.com',
  'marijatodorovicklett@gmail.com',
  'marijatodorovickanic@icloud.com',
]);

export function isMiraseeStaffEmail(email: string | undefined | null): boolean {
  if (!email) {
    return false;
  }
  const e = email.toLowerCase();
  if (STAFF_DOMAIN_SUFFIXES.some((d) => e.endsWith(d))) {
    return true;
  }
  if (STAFF_EMAILS.has(e)) {
    return true;
  }
  return false;
}

export function isTestAccount(
  email: string | undefined | null,
  name?: string | undefined | null,
): boolean {
  if (!email) {
    return false;
  }
  const e = email.toLowerCase();
  if (KNOWN_TEST_EMAILS.has(e)) {
    return true;
  }
  if (TEST_EMAIL_PATTERNS.some((re) => re.test(e))) {
    return true;
  }
  // Convention: Mirasee testers tag their account name with "test" (any case).
  // Word boundary keeps "Tester Williams" / "Teston" out of the bucket.
  if (name && /\btest\b/i.test(name)) {
    return true;
  }
  return false;
}

export function classifyAccount(
  email: string | undefined | null,
  name?: string | undefined | null,
): AccountKind {
  if (isTestAccount(email, name)) {
    return 'test';
  }
  if (isMiraseeStaffEmail(email)) {
    return 'staff';
  }
  return 'member';
}
